import type { AccountDto, AccountKind } from '@koverta/shared';
import { useState } from 'react';
import { useToast } from '../components/toast';
import { Btn, Dialog, Seg } from '../components/ui';
import { errorMessage, patch } from '../lib/api';
import { useHousehold } from '../lib/household';
import { invalidateScopes } from '../lib/queries';

export const KIND_LABEL: Record<AccountKind, string> = {
  checking: 'Checking',
  savings: 'Savings',
  cash: 'Cash',
  credit: 'Credit card',
  asset: 'Asset',
  liability: 'Other debt',
  loan: 'Loan',
};

/** Kinds an existing account can switch between; a loan has terms, so it is only ever added as a loan. */
const EDITABLE_KINDS: AccountKind[] = ['checking', 'savings', 'cash', 'credit', 'asset', 'liability'];
const TRACKING_DEFAULT: AccountKind[] = ['asset', 'liability'];

/** Rename an account, change its type, move an unused one in or out of the budget, or close it. */
export function EditAccountDialog({ account, onClose }: { account: AccountDto; onClose: () => void }) {
  const { fmt } = useHousehold();
  const { toast } = useToast();
  const [name, setName] = useState(account.name);
  const [institution, setInstitution] = useState(account.institution);
  const [kind, setKind] = useState<AccountKind>(account.kind);
  const [onBudget, setOnBudget] = useState(account.onBudget);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const loan = account.kind === 'loan';

  const run = async (body: Record<string, unknown>, done: string) => {
    setBusy(true);
    setErr(null);
    try {
      await patch(`/api/accounts/${account.id}`, body);
      invalidateScopes(['accounts', 'plan', 'transactions', 'activity']);
      toast(done);
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    }
    setBusy(false);
  };
  const save = () => {
    if (!name.trim()) return setErr('Name the account');
    return run({ name, institution, ...(loan ? {} : { kind, onBudget }) }, `Saved ${name.trim()}`);
  };
  const toggleClosed = () => {
    if (
      !account.closed &&
      account.working !== 0 &&
      !confirm(
        `${account.name} still has a balance of ${fmt.money(account.working, account.currency, 2)}. Close it anyway?`,
      )
    )
      return;
    return run({ closed: !account.closed }, account.closed ? 'Reopened' : 'Closed');
  };
  const moving = onBudget !== account.onBudget;

  return (
    <Dialog
      title="Edit account"
      onClose={onClose}
      size="md"
      footer={
        <>
          <Btn variant="ghost" disabled={busy} onClick={toggleClosed}>
            {account.closed ? 'Reopen account' : 'Close account'}
          </Btn>
          <span className="sp" />
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="primary" disabled={busy} onClick={save}>
            Save
          </Btn>
        </>
      }
    >
      <div className="fgrid">
        <label className="field">
          Name
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
        </label>
        <label className="field">
          Bank / institution
          <input
            className="input"
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            maxLength={60}
          />
        </label>
        <label className="field">
          Type
          <select
            className="select"
            value={kind}
            disabled={loan}
            onChange={(e) => {
              const k = e.target.value as AccountKind;
              setKind(k);
              // Suggest where the new type usually lives; it can still be changed below.
              if (account.kind !== k) setOnBudget(!TRACKING_DEFAULT.includes(k));
            }}
          >
            {(loan ? ['loan' as const] : EDITABLE_KINDS).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
        <div className="field">
          Counts as
          {loan ? (
            <span className="muted" style={{ padding: '8px 0' }}>
              Tracking (loans are always outside the budget)
            </span>
          ) : (
            <Seg
              value={onBudget ? 'budget' : 'tracking'}
              onChange={(v) => setOnBudget(v === 'budget')}
              label="Budget or tracking"
              options={[
                ['budget', 'Budget'],
                ['tracking', 'Tracking'],
              ]}
            />
          )}
        </div>
      </div>
      <div className="muted" style={{ fontSize: 12.5 }}>
        {loan
          ? 'Edit the amount borrowed, rate, installment and what is still owed in the loan panel (Edit loan).'
          : moving
            ? `Moving an account ${onBudget ? 'into' : 'out of'} the budget only works while it has nothing but its starting balance; its starting balance ${onBudget ? 'goes to Ready to Assign' : 'leaves Ready to Assign'}. An account in use: close it and add a new one.`
            : 'Changing the type within the budget or within tracking keeps every transaction as it is.'}
      </div>
      {err ? <div className="err-t">{err}</div> : null}
    </Dialog>
  );
}
